import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@repo/api-client";
import type {
  CreatePracticeSessionRequest,
  PracticeLibraryItem,
  PracticeRecommendation,
  PracticeSession,
  PracticeStats,
  GuidedPractice,
} from "@repo/shared-types";

interface PracticeDashboardData {
  sessions: PracticeSession[];
  stats: PracticeStats | null;
  loading: boolean;
  saving: boolean;
  error: string;
  refresh: () => Promise<void>;
  createSession: (payload: CreatePracticeSessionRequest) => Promise<void>;
  weeklyMinutes: number;
  practiceDates: string[];
  practiceLibrary: PracticeLibraryItem[];
  practiceRecommendations: PracticeRecommendation[];
  guidedPractices: GuidedPractice[];
}

function getWeekStart(input: Date): Date {
  const result = new Date(input);
  const day = result.getDay();
  result.setDate(result.getDate() - day);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function usePracticeDashboard(): PracticeDashboardData {
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [stats, setStats] = useState<PracticeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [practiceLibrary, setPracticeLibrary] = useState<PracticeLibraryItem[]>([]);
  const [practiceRecommendations, setPracticeRecommendations] = useState<PracticeRecommendation[]>([]);
  const [guidedPractices, setGuidedPractices] = useState<GuidedPractice[]>([]);

  const refresh = useCallback(async () => {
    setError("");
    const [sessionData, statsData, libraryData, recommendationData, guidedData] = await Promise.all([
      api.listPracticeSessions(365),
      api.getPracticeStats(),
      api.getPracticeLibrary(),
      api.getPracticeRecommendations(),
      api.getGuidedPractices(),
    ]);
    setSessions(sessionData);
    setStats(statsData);
    setPracticeLibrary(libraryData.practices);
    setPracticeRecommendations(recommendationData.recommendations);
    setGuidedPractices(guidedData);
  }, []);

  useEffect(() => {
    refresh()
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load practice data");
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const createSession = useCallback(async (payload: CreatePracticeSessionRequest) => {
    setSaving(true);
    setError("");
    try {
      await api.createPracticeSession(payload);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save practice session");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  const weeklyMinutes = useMemo(() => {
    const weekStart = getWeekStart(new Date());
    return sessions
      .filter((session) => new Date(session.completed_at) >= weekStart)
      .reduce((sum, session) => sum + session.duration_minutes, 0);
  }, [sessions]);

  const practiceDates = useMemo(() => {
    return Array.from(
      new Set(sessions.map((session) => session.completed_at.slice(0, 10)))
    );
  }, [sessions]);

  return {
    sessions,
    stats,
    loading,
    saving,
    error,
    refresh,
    createSession,
    weeklyMinutes,
    practiceDates,
    practiceLibrary,
    practiceRecommendations,
    guidedPractices,
  };
}
